import { Outlet, Link, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';
import { OfflineSyncBanner } from './OfflineSyncBanner';
import { GpsTracker } from '@/components/GpsTracker';
import { useAuthStore } from '@/stores/auth';
import { useLogout } from '@/hooks/useAuth';
import { useState, useEffect, useRef } from 'react';
import { requestNotificationPermission } from '@/lib/notifications';
import { useNotificationPoller } from '@/hooks/useNotificationPoller';
import { ToastContainer } from '@/components/ui/ToastContainer';
import { FloatingActions } from '@/components/ui/FloatingActions';
import { OnboardingTour } from '@/components/ui/OnboardingTour';
import { useOnboardingStore } from '@/stores/onboarding';

export function AppLayout() {
  const employee = useAuthStore((s) => s.employee);
  const logout = useLogout();
  const [menuOpen, setMenuOpen] = useState(false);
  const { unreadCount } = useNotificationPoller();
  const location = useLocation();
  const startOnboarding = useOnboardingStore((s) => s.start);
  const isOnboardingComplete = useOnboardingStore((s) => s.isComplete);

  // Request notification permission once on mount
  const permissionRequested = useRef(false);
  useEffect(() => {
    if (!permissionRequested.current) {
      permissionRequested.current = true;
      requestNotificationPermission();
      // Start onboarding for first-time users
      if (!isOnboardingComplete) {
        setTimeout(() => startOnboarding(), 800);
      }
    }
  }, []);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Global GPS tracker — runs on every page */}
      <GpsTracker />

      {/* Toast notifications */}
      <ToastContainer />

      {/* Floating action button (mobile) */}
      <FloatingActions />

      {/* Onboarding tour for first-time users */}
      <OnboardingTour />

      {/* Desktop sidebar */}
      <Sidebar unreadCount={unreadCount} />

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Offline sync status banner */}
        <OfflineSyncBanner />

        {/* Top header */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 h-14 flex items-center justify-between shrink-0 z-30">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 lg:hidden">
            <div className="w-7 h-7 bg-brand-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xs">ST</span>
            </div>
            <span className="text-base font-bold text-brand-blue-600">Swarnim Track</span>
          </div>

          {/* Desktop - page area (empty, pages have their own headers) */}
          <div className="hidden lg:block" />

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            <Link
              to="/notifications"
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors relative"
              aria-label="Notifications"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setMenuOpen((prev) => !prev)}
                className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-7 h-7 bg-brand-gold-100 text-brand-gold-700 rounded-full flex items-center justify-center text-sm font-bold">
                  {employee?.name.charAt(0).toUpperCase() ?? '?'}
                </div>
                <svg className="w-4 h-4 text-gray-400 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {menuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setMenuOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-20 fade-in">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{employee?.name}</p>
                      <p className="text-xs text-gray-500">{employee?.empId} &middot; {employee?.department}</p>
                    </div>
                    <Link
                      to="/change-pin"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setMenuOpen(false)}
                    >
                      Change PIN
                    </Link>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        logout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto pb-20 lg:pb-6 px-4 lg:px-6 py-4 lg:py-6">
          <div key={location.pathname} className="page-enter">
            <Outlet />
          </div>
        </main>

        {/* Mobile bottom nav */}
        <BottomNav unreadCount={unreadCount} />
      </div>
    </div>
  );
}
